import React from 'react';
import { Box, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import GlassElement from '../../assets/HeroImg.png'; 
import GlassElementMobile from '../../assets/GlassElementMobile1.png'; 

interface HeroSectionProps {
  isDark: boolean;
}

const HeroSection: React.FC<HeroSectionProps> = ({ isDark }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- THEME COLORS ---
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const textPrimary = isDark ? '#FFFFFF' : '#000000'; // Pure black for the poster look
  const borderColor = isDark ? alpha('#FFFFFF', 0.1) : alpha(deepGreen, 0.1);

  // --- MOBILE VIEW (Poster Style) ---
  if (isMobile) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          position: 'relative',
          bgcolor: isDark ? '#0A0A0A' : '#F9F9F9', // Matching the clean poster background
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Inter", sans-serif',
          padding: '20px',
        }}
      >
        {/* Top Left Title: Poster Style */}
        <Box sx={{ position: 'absolute', top: '30px', left: '20px', zIndex: 3 }}>
          <Typography
            sx={{
              fontSize: '3.5rem', // Larger, bolder like the image
              fontWeight: 900,
              lineHeight: 0.85,
              color: textPrimary,
              letterSpacing: '-0.04em',
              textTransform: 'uppercase',
            }}
          >
            WFH
          </Typography>
          <Typography
            sx={{
              fontSize: '3.5rem',
              fontWeight: 900,
              lineHeight: 0.8,
              color: 'transparent',
              WebkitTextStroke: `1.5px ${textPrimary}`,
              letterSpacing: '-0.05em',
              textTransform: 'uppercase',
              pt:1
            }}
          >
            CAFE
          </Typography>
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 500,
              color: textPrimary,
              mt: 1,
              letterSpacing: '0.02em',
              px:1
            }}
          >
            @wfhcafee
          </Typography>
        </Box>

        {/* Top Right Logo/Small Text */}
        <Box sx={{ position: 'absolute', top: '30px', right: '20px', zIndex: 3, textAlign: 'right' }}>
          <Typography sx={{ fontSize: '0.85rem', color: textPrimary, fontWeight: 700, mb: 0.2 }}>
            WFH
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: textPrimary, lineHeight: 1.2, opacity: 0.8 }}>
            Community &<br/>Creative<br/>Workspace
          </Typography>
        </Box>

        {/* Center/Main Fluid Shape */}
        <Box
          component="img"
          src={GlassElementMobile}
          alt="Fluid Shape"
          sx={{
            position: 'absolute',
            top: '45%',
            left: '55%',
            transform: 'translate(-50%, -50%)',
            width: '90%', // Slightly oversized to bleed off edges like the image
            zIndex: 2,
            pointerEvents: 'none',
          }}
        />

        {/* Bottom Left Content */}
        <Box sx={{ position: 'absolute', bottom: '80px', left: '20px', zIndex: 3 }}>
          <Typography sx={{ fontSize: '0.7rem', color: textPrimary, mb: 2, fontWeight: 600 }}>
            \\ 01
          </Typography>
          
          <Typography sx={{ fontSize: '2.5rem', color: textPrimary, lineHeight: 1, mb: 2 }}>
            ***
          </Typography>

          <Typography sx={{ 
            fontSize: '0.75rem', 
            color: textPrimary, 
            lineHeight: 1.4, 
            maxWidth: '240px', 
            mb: 3,
            fontWeight: 500 
          }}>
            Discover the best cafes near you, join niche communities, and share your journey through a real-time social feed.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: '0.8rem', color: textPrimary, fontWeight: 800 }}>
              → SOCIAL WORKSPACE
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: textPrimary, fontWeight: 800 }}>
              → GLOBAL NETWORK
            </Typography>
          </Box>
        </Box>

        {/* Bottom Right Barcode Area */}
        <Box sx={{ 
          position: 'absolute', 
          bottom: '70px', 
          right: '20px', 
          zIndex: 3, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-end' 
        }}>
          <Box
  component="img"
  src="https://salmon-selective-jackal-307.mypinata.cloud/ipfs/bafkreia7mwpy2iztgsps77cqcyrogtt56slnzojghz5epeay4ivmmmttpa"
  alt="Barcode"
  sx={{
    width: '100%',
    height: '40px',
    opacity: 0.6,
    filter: isDark ? 'invert(1)' : 'invert(0)',
  }}
/>
          <Typography sx={{ fontSize: '0.6rem',mt:1 , color: textPrimary, fontWeight: 700, letterSpacing: '0.2em' }}>
            JOIN NOW
          </Typography>
        </Box>
      </Box>
    );
  }

  // --- DESKTOP VIEW (Remains exactly the same) ---
  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        position: 'relative',
        bgcolor: isDark ? '#000000' : '#FAFAFA',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',    
        fontFamily: '"Inter", sans-serif',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
        <Box sx={{ position: 'absolute', top: '20%', width: '100%', height: '1px', bgcolor: borderColor }} />
        <Box sx={{ position: 'absolute', bottom: '25%', width: '100%', height: '1px', bgcolor: borderColor }} />
        <Box sx={{ position: 'absolute', left: '10%', height: '100%', width: '1px', bgcolor: borderColor }} />
        <Box sx={{ position: 'absolute', right: '10%', height: '100%', width: '1px', bgcolor: borderColor }} />
      </Box>

      <Box sx={{ position: 'absolute', top: '8%', left: '8%', maxWidth: '280px', zIndex: 2, textAlign: 'left' }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: textPrimary, mb: 1, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Social Workspace
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: alpha(textPrimary, 0.7), lineHeight: 1.5 }}>
          Discover the best cafes near you, join niche communities, and share your journey through a real-time social feed.
        </Typography>
      </Box>

      <Box sx={{ position: 'absolute', bottom: '12%', right: '6%', maxWidth: '250px', textAlign: 'right', zIndex: 2 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: textPrimary, mb: 1, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Connect & Post
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: alpha(textPrimary, 0.7), lineHeight: 1.5 }}>
          Create your community like Reddit, interact with posts like X, and chat with anyone in our global network.
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', textAlign: 'center', zIndex: 1, width: '100%', mt: -5 }}>
        <Typography variant="h1" sx={{ fontSize: '20vw', fontWeight: 900, lineHeight: 0.8, color: textPrimary, letterSpacing: '-0.05em', textTransform: 'uppercase', mb: 6 }}>
          WFH
        </Typography>
        <Typography variant="h1" sx={{ fontSize: '20vw', fontWeight: 900, lineHeight: 0.8, color: 'transparent', WebkitTextStroke: `1.5px ${textPrimary}`, letterSpacing: '-0.05em', textTransform: 'uppercase' }}>
          CAFE
        </Typography>

        <Box
          component="img"
          src={GlassElement}
          alt="Glass Element"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '38%',
            zIndex: 10,
            filter: 'drop-shadow(0px 30px 60px rgba(0,0,0,0.3))',
            pointerEvents: 'none',
            animation: 'float 7s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translate(-50%, -50%) rotate(0deg)' },
              '50%': { transform: 'translate(-50%, -54%) rotate(2deg)' },
            },
          }}
        />
      </Box>

      <Box sx={{ position: 'absolute', bottom: '5%', left: '3%', display: 'flex', flexDirection: 'column', gap: 0.5, zIndex: 2 }}>
        <Typography sx={{ fontSize: '0.55rem', color: textPrimary, opacity: 0.5 }}>
          LAT: 40.7128° N // LONG: 74.0060° W
        </Typography>
        <Box sx={{ 
          width: '80px', height: '18px', bgcolor: textPrimary, 
          mask: 'url(https://upload.wikimedia.org/wikipedia/commons/e/e9/UPC-A-012345678905.svg) no-repeat center',
          WebkitMask: 'url(https://upload.wikimedia.org/wikipedia/commons/e/e9/UPC-A-012345678905.svg) no-repeat center',
          opacity: 0.6 
        }} />
      </Box>

      {/* <Typography sx={{ position: 'absolute', bottom: '4%', right: '3%', fontSize: '2rem', color: goldColor, fontWeight: 100, zIndex: 2 }}>
        ✦
      </Typography> */}
    </Box>
  );
};

export default HeroSection;